import { useState } from 'react';
import { CiImageOn } from 'react-icons/ci';
import { FaRegTrashAlt } from 'react-icons/fa';
import { IoIosClose } from 'react-icons/io';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { saveCase, setAlert } from '../../../actions/warranty';
import Button from '../../../components/ui/Button/Button';
import { RequiredField } from '../../../utils/components';
import {
  emailReceiver,
  fileTypes,
  formatMySQLdate,
  getEmailBody,
  validateCase,
} from '../../../utils/constants';
import TextEditor from './TextEditor';

const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const urlPattern = /(?:https?:\/\/|ftp:\/\/|www\.|\b[a-z0-9-]+\.)[a-z0-9.-]+(?:\.[a-z]{2,})(?:[/:?#][^\s<]*)?/i;

const CaseIssue = ({
  data,
  changeStep,
  step,
  warrantyData,
  warranty: { loadingCase },
  setAlert,
  saveCase,
}) => {
  const [required, setRequired] = useState({
    case_description: { filled: false, message: '' },
    files: { filled: false, message: '' },
  });
  const [formData, setFormData] = useState({
    case_description: '',
    redirect: null,
  });
  const [files, setAttachments] = useState([]);

  const { redirect, case_description } = formData;

  const fileValidation = (allFiles) => {
    const validated = [];

    allFiles.forEach((file) => {
      let isValidType = false;
      let isValidSize = false;
      fileTypes.forEach((type) => {
        if (file.type.indexOf(type) >= 0) {
          isValidType = true;
        }
      });
       if (file.size <= MAX_FILE_SIZE) isValidSize = true; // <= 10 MB
      if (!isValidType) {
        setAlert(`Incorrect file ${file.name} type`, 'error');
      }
      if (!isValidSize) {
         setAlert(`File ${file.name} size more than 10 MB`, 'error');
      }

      if (isValidType && isValidSize) {
        validated.push(file);
      }
    });
    if (validated.length) {
      if (validated.length + files.length > 5) {
        setAlert(`Maximum 5 files attached`, 'error');
        return;
      }
      setAttachments([...files, ...validated]);
    }
  };

  const getFile = (e) => {
    if (!e.target.files) return;
    const allFiles = [...e.target.files];
    if (!allFiles.length) return;
    fileValidation(allFiles);
  };

  const deleteFile = (e) => {
    const fileIndex = +e.currentTarget.getAttribute('data-index');
    setAttachments(files.filter((file, i) => i !== fileIndex));
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      fileValidation([...e.dataTransfer.files]);
    }

    if (e.currentTarget.classList.contains('dragable-box')) {
      e.currentTarget.classList.remove('dragable-box');
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
  };

  const onDragEnter = (e) => {
    if (!e.currentTarget.classList.contains('dragable-box')) {
      e.currentTarget.classList.add('dragable-box');
    }
  };
  const onDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;

    if (!e.target.classList.contains('case-addfile-box')) return;
    if (e.currentTarget.classList.contains('dragable-box')) {
      e.currentTarget.classList.remove('dragable-box');
    }
  };

  const createCase = async (e) => {
    const validateData = validateCase({
      old: required,
      values: formData,
      step,
    });
    setRequired(
      files.length
        ? validateData.newValues
        : {
            ...validateData.newValues,
            files: { filled: true, message: 'Please, add at least 1 photo' },
          },
    );

    const descriptionText = document
      .querySelector('.text-editor-box')
      ?.innerText.trim() || '';
    if (
      validateData.preventChange ||
      !files.length ||
      descriptionText.length > MAX_DESCRIPTION_LENGTH ||
      urlPattern.test(descriptionText)
    ) {
      if (descriptionText.length > MAX_DESCRIPTION_LENGTH) {
        setRequired((current) => ({
          ...current,
          case_description: {
            filled: true,
            message: 'Description must be no more than 1000 characters',
          },
        }));
        setAlert('Description must be no more than 1000 characters', 'error');
      } else if (urlPattern.test(descriptionText)) {
        setRequired((current) => ({
          ...current,
          case_description: {
            filled: true,
            message: 'Links are not allowed in the description',
          },
        }));
        setAlert('Links are not allowed in the description', 'error');
      }
      return;
    }

    const body = getEmailBody({
      ...data,
      case_description,
      product_sku: warrantyData.sku,
      product_name: warrantyData.name,
    });

    const mailData = {
      email: emailReceiver,
      subject: 'New case from the Gritr Warranty Page',
      body,
      type: 'html',
      files,
    };

    const caseFields = [
      'product_id',
      'address',
      'city',
      'country',
      'state',
      'zip',
    ];
    const caseData = {
      date: formatMySQLdate(new Date()),
      case_description,
      warranty_id: warrantyData.id,
    };
    caseFields.forEach((field) => {
      if (data[field]) caseData[field] = data[field];
    });

    const result = await saveCase({ mailData, caseData });
    if (result && result.error) {
      if (
        result.error.toLowerCase().includes('description') ||
        result.error.toLowerCase().includes('link')
      ) {
        setRequired((current) => ({
          ...current,
          case_description: { filled: true, message: result.error },
        }));
      }
      return;
    }
    if (result) {
      setFormData({ ...formData, redirect: <Redirect to='/' /> });
    }
  };

  if (redirect) return redirect;

  return (
    <div
      className={`register-form-box ${
        step === 4 ? 'register-active-form' : ''
      }`}
    >
      <h3 className='register-list-row-active'>Product Issue</h3>
      <ul className='case-issue-list'>
        <li className='register-form-row'>
          <label className='register-form-select__label'>
            Warranty Request Description
            <RequiredField />
          </label>
          <TextEditor
            valid={required.case_description.filled}
            formData={formData}
            setFormData={setFormData}
          />
          <div className='case-addfile-error'>
            <IoIosClose
              style={{
                display: required.case_description.message ? '' : 'none',
                width: '15px',
                height: 'auto',
                color: '#FF4929',
              }}
            />
           <p className='register-error-message'>
             {required.case_description.message}
           </p>
          </div>
        </li>
        <li className='register-form-row'>
          <label className='register-form-select__label'>
            Attach Photos
            <RequiredField />
          </label>
          <div
            style={{
              border: required.files.message
                ? '1px dashed #FF4929'
                : '1px dashed #FFFFFF',
            }}
            className='case-addfile-container'
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
          >
            <CiImageOn
              style={{ width: '70px', height: '70px', color: '#666666' }}
            />
            <div className='case-addfile-main'>
              <div className={`case-addfile-box`}>
                <input
                  onChange={getFile}
                  type='file'
                  multiple
                  id='add-files'
                   accept='image/jpeg,image/png,image/gif,image/webp'
                  disabled={loadingCase}
                />
                <label htmlFor='add-files' className='case-upload-button'>
                  Add file
                </label>
                <span>or drop files here</span>
              </div>
            </div>
          </div>
          <div className='case-attached-box'>
            {files.map((file, i) => (
              <p className='case-attached-file' key={`attachment-${i}`}>
                {file.name}
                <FaRegTrashAlt
                  data-index={i}
                  onClick={deleteFile}
                  className='case-attached-delete'
                />
              </p>
            ))}
          </div>
          <div className='case-addfile-error'>
            <IoIosClose
              style={{
                display: required.files.message ? '' : 'none',
                width: '15px',
                height: 'auto',
                color: '#FF4929',
              }}
            />
            <p className='register-error-message'>{required.files.message}</p>
          </div>
        </li>
      </ul>
      <div className='register-form-btns'>
        <Button
          data-index='3'
          onClick={changeStep}
          variant='secondary'
          className='register-form-button'
        >
          BACK
        </Button>
        <Button
          onClick={createCase}
          className='register-form-button'
          disabled={loadingCase}
        >
          SUBMIT
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  warranty: state.warranty,
});

export default connect(mapStateToProps, { setAlert, saveCase })(CaseIssue);
